import { useRef } from 'react';
import debounce from 'lodash/debounce';
import { OptionType } from '../../../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { authFetch } from '../../../../../../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../../../../../../CommonUtils/urlWithParams';
import { AssetClassEnum } from '../../../../../../api/useDefaultAssetAllocationQuery';

export interface SymbolOption extends OptionType<string | number> {
  data: {
    productId: number | null;
    ticker: string;
    name: string;
    price: number;
    assetClass: AssetClassEnum;
    assetClasses: Partial<Record<AssetClassEnum, number>>;
  };
}

export interface FetcherParams {
  clientId: number;
  search: string;
}

interface AvailableProductDTO {
  Id: number;
  Ticker: string;
  ProductName: string;
  Price: number;
  AssetClass: AssetClassEnum;
  AssetClasses?: Partial<Record<AssetClassEnum, number>> | Record<string, number>;
}

const normalizeAssetClasses = (
  assetClasses: Partial<Record<AssetClassEnum, number>> | Record<string, number> | undefined,
): Partial<Record<AssetClassEnum, number>> | undefined => {
  if (assetClasses == null) {
    return undefined;
  }

  return Object.entries(assetClasses).reduce((acc, [key, value]) => {
    acc[Number(key) as AssetClassEnum] = value;

    return acc;
  }, {} as Partial<Record<AssetClassEnum, number>>);
};

let controller;

export const searchSymbols = async ({
  clientId,
  search,
}: FetcherParams): Promise<SymbolOption[]> => {
  if (controller) {
    controller.abort();
  }

  controller = new AbortController();

  try {
    const { AvailableProducts } = await authFetch(
      urlWithParams('/Consumer/AccountPositions/AvailableProducts', {
        clientId,
        search: search || '',
      }),
      { signal: controller.signal },
    );

    return (AvailableProducts as AvailableProductDTO[]).map(
      ({ Id, Ticker, ProductName, Price, AssetClass, AssetClasses }) => {
        const normalizedAssetClasses = normalizeAssetClasses(AssetClasses);

        return {
          label: Ticker || ProductName,
          value: Ticker || ProductName,
          subLabel: ProductName,
          data: {
            productId: Id,
            ticker: Ticker,
            name: ProductName,
            price: Price,
            assetClass: AssetClass,
            assetClasses:
              normalizedAssetClasses && Object.keys(normalizedAssetClasses).length > 0
                ? normalizedAssetClasses
                : { [AssetClass]: 100 },
          },
        };
      },
    );
  } catch (error) {
    console.error(error);

    return [];
  }
};

interface Params {
  clientId: number;
}

export const useSearchSymbolsSelect = ({ clientId }: Params) => {
  const loadSymbols = useRef(
    debounce((search, callback) => {
      searchSymbols({ clientId, search }).then(opts => callback(opts));
    }, 300),
  ).current;

  return {
    loadSymbols,
  };
};
